import React from 'react';
import ReactDOM from 'react-dom/client';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './index.css';

// Parse URL parameters
const params = new URLSearchParams(window.location.search);
const caseId = params.get('caseId') || params.get('case_id') || '';
const caseIdentifier = params.get('caseIdentifier') || params.get('patientId') || '';
const sampleType = params.get('sampleType') || '';
const scanDate = params.get('scanDate') || '';
const slideId = params.get('slideId') || '';
const tileBase = params.get('tileBase') || '';

// Check if this is a viewer request
const isViewerRequest = !!(caseId || tileBase);

const toIsoStringOrNow = (value: unknown): string => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString();
  }

  const raw = String(value ?? '').trim();
  if (!raw || raw.toLowerCase() === 'n/a' || raw.toLowerCase() === 'na' || raw.toLowerCase() === 'null') {
    return new Date().toISOString();
  }

  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return new Date().toISOString();
  }

  return parsed.toISOString();
};

interface SlideData {
  caseSlideID?: string;
  slideImageURNPath?: string;
  specimenType?: string;
  stainType?: string;
  scanDate?: string;
  caseSlideDisplayName?: string;
}

interface CaseData {
  id: string;
  patientId: string;
  sampleType: string;
  specimenType?: string;
  sourceOfSpecimen?: string;
  scannedAt: string;
  slides?: SlideData[];
  caseSlides?: SlideData[];
}

if (isViewerRequest) {
  console.log('Standalone Viewer Mode detected');
  console.log({ caseId, caseIdentifier, sampleType, tileBase: tileBase ? tileBase.substring(0, 100) + '...' : 'none' });
  
  // Function to render viewer with caseData
  const renderViewer = (caseData: CaseData | null) => {
    import('./components/VisionXViewer').then(({ default: VisionXViewer }) => {
      // Use fetched data or fall back to URL params
      const finalCaseData = caseData || {
        id: caseId,
        patientId: caseIdentifier,
        sampleType: sampleType || 'N/A',
        specimenType: sampleType || 'N/A',
        sourceOfSpecimen: sampleType || 'N/A',
        scannedAt: toIsoStringOrNow(scanDate),
        caseSlides: tileBase ? [{
          caseSlideID: slideId || 'SL-001',
          slideImageURNPath: tileBase,
          specimenType: sampleType || 'N/A',
          stainType: 'N/A',
          scanDate: scanDate
        }] : []
      };

      // If we have API data with slides, update them with correct specimenType
      console.log('Slide creation check:', {
        hasCaseData: !!caseData,
        slides: caseData?.slides,
        slidesLength: caseData?.slides?.length,
        hasTileBase: !!tileBase
      });

      if (caseData && caseData.slides && caseData.slides.length > 0) {
        // API returned slides - update them with correct specimenType from case data
        finalCaseData.slides = caseData.slides.map((slide: any) => ({
          ...slide,
          specimenType: caseData.specimenType || slide.specimenType,
          sampleType: caseData.sampleType || slide.sampleType
        }));
        console.log('Updated API slides with case specimenType:', caseData.specimenType);
      } else if (caseData && tileBase) {
        // API returned no slides, create them with correct specimenType from API
        finalCaseData.caseSlides = [{
          caseSlideID: slideId || 'SL-001',
          slideImageURNPath: tileBase,
          specimenType: caseData.specimenType || caseData.sampleType || sampleType || 'N/A',
          stainType: 'N/A',
          scanDate: scanDate
        }];
        console.log('Created slide with API specimenType:', caseData.specimenType);
      } else {
        console.log('Using fallback slides from URL params');
      }
      
      // Ensure caseData has specimenType field for VisionXViewer
      const caseDataForViewer = {
        ...finalCaseData,
        specimenType: finalCaseData.specimenType || finalCaseData.sampleType,
        sourceOfSpecimen: finalCaseData.sourceOfSpecimen || finalCaseData.specimenType || finalCaseData.sampleType
      };

      console.log('Rendering VisionXViewer with caseData:', {
        id: caseDataForViewer.id,
        patientId: caseDataForViewer.patientId,
        specimenType: caseDataForViewer.specimenType,
        slidesCount: caseDataForViewer.caseSlides?.length || caseDataForViewer.slides?.length || 0
      });

      const rootElement = document.getElementById('root');
      const viewerCaseData = caseData ? caseDataForViewer : undefined;
      if (!rootElement) {
        const rootDiv = document.createElement('div');
        rootDiv.id = 'root';
        document.body.style.margin = '0';
        document.body.style.padding = '0';
        document.body.style.background = '#1a1a2e';
        document.body.appendChild(rootDiv);
        const root = ReactDOM.createRoot(rootDiv);
        root.render(
          <React.StrictMode>
            <VisionXViewer
              caseId={caseDataForViewer.id.toString()}
              onClose={() => window.close()}
              caseData={viewerCaseData as any}
            />
          </React.StrictMode>
        );
      } else {
        const root = ReactDOM.createRoot(rootElement);
        root.render(
          <React.StrictMode>
            <VisionXViewer
              caseId={caseDataForViewer.id.toString()}
              onClose={() => window.close()}
              caseData={viewerCaseData as any}
            />
          </React.StrictMode>
        );
      }
    });
  };
  
  if (tileBase) {
    // A direct tileBase is enough to open the slide. Rendering immediately avoids
    // blocking the viewer on slow metadata calls or Azure container cold starts.
    console.log('Using tileBase from URL immediately');
    renderViewer(null);
  } else if (caseId) {
    // Render the viewer shell immediately. VisionXViewer will fetch case details
    // itself when tileBase is missing; doing it here first blocks the new tab on
    // a duplicate /cases/details call and leaves users staring at a splash screen.
    console.log('Rendering viewer immediately; case details will hydrate in viewer');
    renderViewer(null);
  } else {
    console.log('Neither caseId nor tileBase provided, cannot render viewer');
  }
} else {
  // No viewer params - load normal app
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error("Could not find root element to mount to");
  }
  
  // Dynamically import App to avoid circular dependencies
  import('./App').then(({ default: App }) => {
    const root = ReactDOM.createRoot(rootElement);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  });
}
